import React from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { MdArrowBack, MdEdit } from 'react-icons/md';
import { 
  activateIntegrationRequest, 
  deactivateIntegrationRequest,
  fetchIntegrationsRequest 
} from '../../redux/slices/integrationsSlice';
import { getIntegrationStatus } from '../../utils/oauthHelper';

import type { RootState } from '../../redux/store';
import type { Integration } from './types';

interface IntegrationDetailProps {
  integration: Integration;
  onBack: () => void;
}

const IntegrationDetail: React.FC<IntegrationDetailProps> = ({ integration, onBack }) => {
  const dispatch = useDispatch();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  
  // Get state from Redux
  const { data: apiIntegrations, activating, loading } = useSelector(
    (state: RootState) => state.integrations
  );

  // Get real integration data from API
  const { isActive, integration: apiIntegration } = getIntegrationStatus(
    apiIntegrations,
    integration.type
  );

  const isConnected = isActive;
  const isActivating = activating === integration.type;

  // Handle activate/deactivate
  const handleToggle = () => {
    if (!restaurantId) return;
    
    if (isConnected && apiIntegration) {
      // Deactivate
      dispatch(deactivateIntegrationRequest({
        restaurantId,
        integrationId: apiIntegration.id
      }));
    } else {
      // Activate (opens OAuth popup)
      dispatch(activateIntegrationRequest({
        platform: integration.type,
        restaurantId
      }));
    }
  };

  // Mock account data - use real data if available
  const accountInfo = {
    email: isConnected ? (apiIntegration?.configurations?.email || 'Connected Account') : null,
    accountId: isConnected ? (apiIntegration?.id?.toString() || `${integration.type}_id`) : null,
    connectedAt: isConnected ? (apiIntegration?.configurations?.connected_at || 'Recently') : null,
  };

  return (
    <div className=" bg-black p-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-300 hover:text-[#EE4423] transition-colors mb-6"
        >
          <MdArrowBack size={20} />
          <span className="text-sm font-medium cursor-pointer">Back</span>
        </button>

        {/* Unified Background Container */}
        <div
          className="rounded-xl p-6 "
          style={{
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
            background: '#212121',
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 min-h-[70vh]">
            {/* Left: Integration Card */}
            <motion.div
              initial={{ x: 0, scale: 1 }}
              animate={{ x: 0, scale: 1 }}
              className="lg:col-span-1 flex flex-col"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: integration.color }} >
                <span className="text-white text-3xl">{integration.icon}</span>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed mb-4 flex-1">
                {integration.description}
              </p>
              <button
                onClick={handleToggle}
                disabled={isActivating || loading}
                className={`w-full py-3 cursor-pointer rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isConnected
                    ? 'bg-[#ee4423] hover:bg-[#ee4423] text-white'
                    : 'bg-[#EE4423] hover:bg-[#ee4423] text-white'
                }`}
              >
                {isActivating
                  ? 'Connecting...'
                  : isConnected
                  ? 'Deactivate'
                  : 'Activate'}
              </button>
            </motion.div>

            {/* Right: Configuration Details */}
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="lg:col-span-2"
            >
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
                <h3 className="text-xl font-medium text-[#ee4425]">Admin configuration</h3>
                {/* <button className="flex items-center gap-2 text-[#EE4423] hover:text-[#FF7A59] transition-colors">
                  <MdEdit size={18} />
                  <span className="text-sm font-medium">Edit</span>
                </button> */}
              </div>

              {isConnected ? (
                <>
                  {/* Email Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-gray-400 text-sm font-medium">EMAIL</label>
                        <p className="text-white mt-1">{accountInfo.email}</p>
                      </div>
                      {/* <button className="text-[#EE4423] hover:text-[#FF7A59] text-sm font-medium transition-colors">
                        CHANGE
                      </button> */}
                    </div>
                    <p className="text-gray-400 text-xs">
                      {integration.name} integration is currently active for this email account.
                    </p>
                  </div>

                  {/* Account ID Section */}
                  <div className="mb-6 pb-6 border-b border-gray-700">
                    <label className="text-gray-400 text-sm font-medium block mb-2">ACCOUNT ID</label>
                    <p className="text-gray-300 text-sm font-mono">{accountInfo.accountId}</p>
                  </div>

                  {/* Connected Date */}
                  <div className="mb-6">
                    <label className="text-gray-400 text-sm font-medium block mb-2">CONNECTED SINCE</label>
                    <p className="text-white">{accountInfo.connectedAt}</p>
                  </div>

                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-lg mb-4">
                    This integration is not connected yet.
                  </p>
                  <p className="text-gray-500 text-sm">
                    Click "Activate" to connect your {integration.name} account.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationDetail;
